import LoadingWheel from "../LoadingWheel";
import { useMemo, useState } from "react";

export interface SelectableOption {
  id: string;
  name: string;
}

interface SelectableTableProps {
  options: SelectableOption[];
  isLoading: boolean;
  selectedIds: string[];
  setSelectedIds: (updater: (prev: string[]) => string[]) => void;
  placeholder: string;
}

export default function SelectableTable(props: SelectableTableProps) {
  const {
    options,
    isLoading,
    selectedIds,
    setSelectedIds,
    placeholder,
  } = props;

  const [search, setSearch] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const filtered = useMemo(() => {
    let rows = options ?? [];
    const currentSearch = search;
    if (currentSearch) rows = rows.filter(x => x.name.toLowerCase().includes(currentSearch.toLowerCase()));
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [options, search]);

  const displayed = useMemo(() => {
    if (!showSelectedOnly) return filtered;
    return filtered.filter(o => selectedIds.includes(o.id));
  }, [filtered, showSelectedOnly, selectedIds]);

  const displayedSelectedCount = useMemo(() => {
    return displayed.filter(o => selectedIds.includes(o.id)).length;
  }, [displayed, selectedIds]);

  const toggleRow = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    const allVisibleIds = displayed.map(x => x.id);
    const allVisibleSelected = displayedSelectedCount === allVisibleIds.length && allVisibleIds.length > 0;
    if (allVisibleSelected) {
      setSelectedIds(prev => prev.filter(id => !allVisibleIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...(prev ?? []), ...allVisibleIds])));
    }
  };

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-3">
        <input
          type="text"
          placeholder={placeholder}
          className="border rounded px-3 py-2 w-full mr-3"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="ml-3 whitespace-nowrap flex items-center space-x-4">
          <label className="mr-2 text-sm">Select All</label>
          <input type="checkbox" checked={displayedSelectedCount === (displayed?.length ?? 0) && (displayed?.length ?? 0) > 0} onChange={toggleAll} />
          <div className="ml-4 flex items-center">
            <label className="mr-2 text-sm">Show Selected Only</label>
            <input type="checkbox" checked={showSelectedOnly} onChange={(e) => setShowSelectedOnly(e.target.checked)} />
          </div>
        </div>
      </div>
      <div className="max-h-[32rem] overflow-y-auto border rounded">
        {isLoading ? (
          <div className="p-4"><LoadingWheel /></div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sel</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayed.map(o => (
                <tr key={o.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2"><input type="checkbox" checked={selectedIds.includes(o.id)} onChange={() => toggleRow(o.id)} /></td>
                  <td className="px-3 py-2">{o.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


